/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Table } from 'antd';
import { onShowSizeChange, itemRender } from '../Pagination';
import BusinessHeader from '../BusinessHeader';
import SidebarAdmin from '../SidebarAdmin';
import { imagesend, plusicon, refreshicon, searchnormal } from '../imagepath';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import config from '../../config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const AdminList = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [permissions, setPermissions] = useState(null); // null indicates loading
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [permissionsData, setPermissionsData] = useState({
    add_department: 0,
    view_doctors_list_own: 0,
    view_doctors_list_all: 0,
    add_doctor: 0,
    add_archive_doctor: 0,
    view_archive_doctors_list: 0,
    reactivate_archive_doctor: 0,
    view_admin_users_own: 0,
    view_admin_users_all: 0,
    add_admin_users: 0,
    view_appointments_own: 0,
    view_appointments_all: 0,
    add_appointment: 0,
  });

  // Permission checks
  const hasViewAllPermission = permissions?.view_admin_users_all === 1;
  const hasViewOwnPermission = permissions?.view_admin_users_own === 1;
  const hasEditPermission = permissions?.add_admin_users === 1;
  const hasViewPermission = hasViewAllPermission || hasViewOwnPermission;

  // Fetch permissions
  const fetchPermissions = async () => {
    try {
      const businessID = localStorage.getItem("BusinessID");
      const userID = localStorage.getItem("userUUID");

      if (!businessID || !userID) {
        console.error("Business ID or User ID not found in localStorage");
        setPermissions({
          view_admin_users_own: 0,
          view_admin_users_all: 0,
          add_admin_users: 0
        });
        return;
      }

      const response = await fetch(`${config.API_URL}get_admins_permissions.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_id: businessID, user_id: userID }),
      });

      const data = await response.json();
      if (data.status === "success" && data.permissions) {
        setPermissions(data.permissions);
      } else {
        console.error("Failed to fetch permissions:", data.message);
        setPermissions({
          view_admin_users_own: 0,
          view_admin_users_all: 0,
          add_admin_users: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions({
        view_admin_users_own: 0,
        view_admin_users_all: 0,
        add_admin_users: 0,
      });
    }
  };

  // Row selection
  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // Fetch admins
  const fetchAdmins = async () => {
    try {
      const businessID = localStorage.getItem('BusinessID');
      const userID = localStorage.getItem('userUUID');
      if (!businessID) {
        toast.error('Business ID is missing.', { position: 'top-center' });
        return;
      }

      const response = await fetch(`${config.API_URL}get_business_admins.php?business_id=${businessID}`);
      const result = await response.json();

      if (result.status === 'success') {
        let adminData = result.permissions;

        // Filter admins based on permissions
        if (!hasViewAllPermission && hasViewOwnPermission) {
          adminData = adminData.filter((admin) => admin.created_by === userID);
        }

        const mappedAdmins = adminData.map((admin, index) => ({
          id: admin.user_id || index + 1,
          user_id: admin.user_id,
          Name: admin.full_name || 'N/A',
          Email: admin.email || 'N/A',
          Phone: admin.phone || 'N/A',
          TotalPermissions: admin.total_permissions || 0,
          ProfileImage: admin.profile_image
            ? `${config.API_URL}uploads/profile_images/user_images/${admin.profile_image}`
            : imagesend,
          created_by: admin.created_by || '',
        }));

        setAdmins(mappedAdmins);
      } else {
        console.error('Fetch admins failed:', result.message);
        toast.error('Failed to fetch admins.', { position: 'top-center' });
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Error fetching admins.', { position: 'top-center' });
    }
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
  };

  // Filter admins
  const filteredAdmins = admins.filter((admin) =>
    (admin.Name || '').toLowerCase().includes(searchText) ||
    (admin.Email || '').toLowerCase().includes(searchText) ||
    (admin.Phone || '').toLowerCase().includes(searchText)
  );

  // Delete admin permissions
  const onDeleteClick = (userId) => {
    if (!hasEditPermission) {
      toast.error('You do not have permission to remove admin permissions.', {
        position: 'top-center',
      });
      return;
    }

    toast(
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '16px', fontWeight: 'bold' }}>
          ⚠️ Are you sure you want to remove this admins permissions?
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
          <button
            onClick={async () => {
              try {
                const businessID = localStorage.getItem('BusinessID');
                const response = await fetch(
                  `${config.API_URL}remove_admin.php?business_id=${businessID}&user_id=${userId}`,
                  { method: 'GET' }
                );
                const result = await response.json();

                if (result.status === 'success') {
                  toast.success('Admin permissions removed successfully.', { position: 'top-center' });
                  setAdmins((prevAdmins) => prevAdmins.filter((admin) => admin.user_id !== userId));
                } else {
                  toast.error('Failed to remove admin permissions: ' + result.message, {
                    position: 'top-center',
                  });
                }
              } catch (error) {
                console.error('Error removing admin permissions:', error);
                toast.error('Something went wrong! Please try again.', { position: 'top-center' });
              }
            }}
            style={{
              background: 'green',
              color: 'white',
              border: 'none',
              padding: '5px 15px',
              cursor: 'pointer',
              borderRadius: '5px',
            }}
          >
            Yes
          </button>
          <button
            onClick={() => toast.dismiss()}
            style={{
              background: 'red',
              color: 'white',
              border: 'none',
              padding: '5px 15px',
              cursor: 'pointer',
              borderRadius: '5px',
            }}
          >
            No
          </button>
        </div>
      </div>,
      {
        position: 'top-center',
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false,
      }
    );
  };

  // Fetch admin permissions for editing
  const fetchAdminPermissions = async (user_id) => {
    const businessId = localStorage.getItem("BusinessID");
    if (!businessId || !user_id) {
      console.error("Missing businessId or user_id:", { businessId, user_id });
      toast.error("Cannot fetch permissions: Missing Business ID or User ID.", { position: 'top-center' });
      return;
    }

    try {
      const response = await fetch(`${config.API_URL}get_admins_permissions.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id, business_id: businessId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status === "success" && data.permissions) {
        setPermissionsData(data.permissions);
      } else {
        console.warn("API returned non-success status:", data.message);
        toast.error(data.message || "Failed to load permissions.", { position: 'top-center' });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      toast.error(`Failed to fetch permissions: ${error.message}`, { position: 'top-center' });
    }
  };

  // Handle edit click
  const onEditClick = (admin) => {
    setEditAdmin({
      user_id: admin.user_id,
      Phone: admin.Phone,
      firstName: admin.Name.split(' ')[0] || '',
      lastName: admin.Name.split(' ').slice(1).join(' ') || '',
      Email: admin.Email,
    });
    fetchAdminPermissions(admin.user_id);
    setIsModalOpen(true);
  };

  // Handle permission change
  const handlePermissionChange = (permission) => {
    setPermissionsData((prev) => ({
      ...prev,
      [permission]: prev[permission] ? 0 : 1,
    }));
  };

  // Render permissions (reused from AddAdmin)
  const renderPermissions = () => {
    const permissionGroups = {
      Department: ["add_department"],
      Doctors: [
        "view_doctors_list_own",
        "view_doctors_list_all",
        "add_doctor",
        "add_archive_doctor",
        "view_archive_doctors_list",
        "reactivate_archive_doctor",
      ],
      Admins: ["view_admin_users_own", "view_admin_users_all", "add_admin_users"],
      Appointments: ["view_appointments_own", "view_appointments_all", "add_appointment"],
    };

    const permissionPairs = {
      view_doctors_list_own: "view_doctors_list_all",
      view_doctors_list_all: "view_doctors_list_own",
      view_admin_users_own: "view_admin_users_all",
      view_admin_users_all: "view_admin_users_own",
      view_appointments_own: "view_appointments_all",
      view_appointments_all: "view_appointments_own",
    };

    const handleSelectAll = (e) => {
      const isChecked = e.target.checked;
      const updatedPermissions = {};
      Object.keys(permissionsData).forEach((perm) => {
        updatedPermissions[perm] = isChecked ? 1 : 0;
      });
      setPermissionsData(updatedPermissions);
    };

    const allSelected = Object.values(permissionsData).every((value) => value === 1);

    const isPermissionDisabled = (perm) => {
      if (allSelected) return true;
      if (permissionPairs[perm] && permissionsData[permissionPairs[perm]] === 1) return true;
      return false;
    };

    return (
      <div className="form-group">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4 className="mb-0">Set Permissions</h4>
          <div className="form-check">
            <input
              type="checkbox"
              id="selectAll"
              checked={allSelected}
              onChange={handleSelectAll}
              className="form-check-input"
            />
            <label htmlFor="selectAll" className="form-check-label ms-2">
              Select All
            </label>
          </div>
        </div>
        <div
          className="border p-2 rounded mt-2"
          style={{ height: "400px", overflowY: "auto", border: "1px solid #ddd" }}
        >
          {Object.entries(permissionGroups).map(([group, perms]) => (
            <div key={group} className="mb-3">
              <h6>{group}</h6>
              {perms.map((perm) => (
                <div key={perm} className="form-check">
                  <input
                    type="checkbox"
                    id={perm}
                    checked={permissionsData[perm] === 1}
                    onChange={() => handlePermissionChange(perm)}
                    className="form-check-input"
                    disabled={isPermissionDisabled(perm)}
                  />
                  <label htmlFor={perm} className="form-check-label ms-2">
                    {perm.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                  </label>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasEditPermission) {
      toast.error("You do not have permission to edit admins.", { position: "top-center" });
      return;
    }

    const businessId = localStorage.getItem("BusinessID");
    if (!businessId) {
      toast.error("Missing Business ID.", { position: "top-center" });
      return;
    }

    const payload = {
      business_id: businessId,
      user_id: editAdmin.user_id,
      ...permissionsData,
    };

    try {
      const response = await fetch(`${config.API_URL}update_admin_permissions.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.status === "success") {
        toast.success("Admin permissions updated successfully!", { position: "top-center" });
        setIsModalOpen(false);
        fetchAdmins(); // Refresh admin list
      } else {
        toast.error(result.message || "Failed to update admin permissions.", { position: "top-center" });
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      toast.error("Failed to update admin permissions.", { position: "top-center" });
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      render: (text, record) => (
        <h2 className="profile-image">
          <Link to="#" className="avatar avatar-sm me-2">
            <img className="avatar-img rounded-circle" src={record.ProfileImage} alt="User Image" />
          </Link>
          <Link to="#">{record.Name}</Link>
        </h2>
      ),
      sorter: (a, b) => (a.Name || '').length - (b.Name || '').length,
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      sorter: (a, b) => (a.Email || '').length - (b.Email || '').length,
    },
    {
      title: 'Phone',
      dataIndex: 'Phone',
      sorter: (a, b) => (a.Phone || '').length - (b.Phone || '').length,
      render: (text) => <Link to="#">{text}</Link>,
    },
    {
      title: 'Total Permissions',
      dataIndex: 'TotalPermissions',
      sorter: (a, b) => a.TotalPermissions - b.TotalPermissions,
    },
    {
      title: '',
      dataIndex: 'actions',
      render: (text, record) => (
        <div className="text-end">
          <div className="dropdown dropdown-action">
            <Link
              to="#"
              className="action-icon dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-ellipsis-v" />
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              {hasEditPermission && (
                <>
                  <button
                    className="dropdown-item"
                    onClick={() => onEditClick(record)}
                  >
                    <i className="far fa-edit me-2" />
                    Edit
                  </button>
                  {/* <button
                    className="dropdown-item"
                    onClick={() => onDeleteClick(record.user_id)}
                  >
                    <i className="fas fa-trash-alt me-2" />
                    Delete
                  </button> */}
                </>
              )}
            </div>
          </div>
        </div>
      ),
    },
  ];

  // Fetch permissions and admins on mount
  useEffect(() => {
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (hasViewPermission) {
      fetchAdmins();
    }
  }, [permissions]);

  // Render loading state
  if (permissions === null) {
    return (
      <div className="page-wrapper">
        <BusinessHeader />
        <SidebarAdmin id="menu-item7" id1="menu-items7" activeClassName="admin-list" />
        <div className="content">
          <p>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Render access denied
  if (!hasViewPermission) {
    return (
      <div className="page-wrapper">
        <BusinessHeader />
        <SidebarAdmin id="menu-item7" id1="menu-items7" activeClassName="admin-list" />
        <div className="content">
          <div className="alert alert-danger">
            <strong>Access Denied</strong>! You do not have permission to view admins.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <BusinessHeader />
      <SidebarAdmin id="menu-item7" id1="menu-items7" activeClassName="admin-list" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Admins</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Admin List</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire">
                <div className="card-body">
                  <div className="page-table-header mb-2">
                    <div className="row align-items-center">
                      <div className="col">
                        <div className="doctor-table-blk">
                          <h3>Admin List</h3>
                          <div className="doctor-search-blk">
                            <div className="top-nav-search table-search-blk">
                              <form onSubmit={(e) => e.preventDefault()}>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search here"
                                  value={searchText}
                                  onChange={handleSearch}
                                />
                                <Link className="btn">
                                  <img src={searchnormal} alt="#" />
                                </Link>
                              </form>
                            </div>
                            <div className="add-group">
                              {hasEditPermission && (
                                <Link to="/add-admin" className="btn btn-primary add-pluss ms-2">
                                  <img src={plusicon} alt="#" />
                                </Link>
                              )}
                              <Link to="#" className="btn btn-primary doctor-refresh ms-2" onClick={fetchAdmins}>
                                <img src={refreshicon} alt="#" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive admin-list">
                    <Table
                      pagination={{
                        total: filteredAdmins.length,
                        showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                        onShowSizeChange: onShowSizeChange,
                        itemRender: itemRender,
                      }}
                      columns={columns}
                      dataSource={filteredAdmins}
                      rowSelection={rowSelection}
                      rowKey={(record) => record.id}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Admin Modal */}
      {isModalOpen && editAdmin && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Admin Permissions</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <div className="form-group local-forms">
                        <label>Phone Number <span className="login-danger">*</span></label>
                        <PhoneInput
                          onlyCountries={["in", "gb", "us", "ae", "ng", "lk", "bd"]}
                          country={"in"}
                          value={editAdmin.Phone}
                          disabled
                          className="phoneinput"
                          placeholder="Enter phone number"
                          countryCodeEditable={false}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group local-forms">
                        <label>First Name <span className="login-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={editAdmin.firstName}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group local-forms">
                        <label>Last Name <span className="login-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={editAdmin.lastName}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-group local-forms">
                        <label>Email <span className="login-danger">*</span></label>
                        <input
                          type="email"
                          className="form-control"
                          value={editAdmin.Email}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      {renderPermissions()}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default AdminList;